/**
 * LINKS TO THE EXACT VERSION A TICK RECORDS.
 *
 * The consent step used to carry no link at all — one of the three reasons the
 * held leads are unprovable. These links resolve the CURRENT published version
 * from the register and address it by number, so the page a person opened and
 * the version recorded against their tick are the same object.
 *
 * The label is the register's own title plus its version. No new copy: nothing
 * here asserts anything about retention, consent, money or timing.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { legalDocPath, loadCurrentLegalDocument, type LegalDocKey } from "@/lib/legal-consent";

interface Resolved {
  docKey: string;
  title: string;
  version: number;
}

export function LegalDocLinks({ docKeys, testId }: { docKeys: readonly LegalDocKey[]; testId?: string }) {
  const [docs, setDocs] = useState<Resolved[]>([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all(docKeys.map((k) => loadCurrentLegalDocument(k).catch(() => null)))
      .then((rows) => {
        if (cancelled) return;
        setDocs(
          rows
            .filter((r): r is NonNullable<typeof r> => !!r)
            .map((r) => ({ docKey: r.docKey, title: r.title, version: r.version })),
        );
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [docKeys]);

  if (docs.length === 0) return null;

  return (
    <span className="flex flex-wrap gap-x-4 gap-y-1 text-[14px]" data-testid={testId ?? "legal-doc-links"}>
      {docs.map((d) => (
        <Link
          key={d.docKey}
          to={legalDocPath(d.docKey, d.version)}
          target="_blank"
          rel="noreferrer"
          // The parent consent card is itself a checkbox; opening a document
          // must not toggle the tick.
          onClick={(e) => e.stopPropagation()}
          className="underline decoration-foreground/30 underline-offset-2 hover:decoration-foreground"
          data-testid={`legal-doc-link-${d.docKey}`}
        >
          {d.title} (v{d.version})
        </Link>
      ))}
    </span>
  );
}

export default LegalDocLinks;
